import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import AppLayout from "@/components/app/AppLayout";
import DemoLock from "@/components/app/DemoLock";
import LandingPage from "@/pages/Landing";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import LegalPage from "@/pages/Legal";
import ResourcesPage from "@/pages/Resources";
import ArticlePage from "@/pages/Article";
import AppResourcesPage from "@/pages/AppResources";
import PricingPage from "@/pages/Pricing";
import ResetPasswordPage from "@/pages/ResetPassword";
import PaymentSuccessPage from "@/pages/PaymentSuccess";
import PaymentCancelPage from "@/pages/PaymentCancel";
import PortalLoginPage from "@/pages/PortalLogin";
import PortalActivatePage from "@/pages/PortalActivate";
import MemberActivatePage from "@/pages/MemberActivate";
import PortalLayout from "@/components/portal/PortalLayout";
import {
  PortalHomePage, PortalReceiptsPage, PortalLeasePage,
  PortalIncidentsPage, PortalProfilePage, PortalLettersPage,
  PortalRegularisationsPage,
} from "@/pages/PortalPages";
import DashboardPage from "@/pages/Dashboard";
import OnboardingPage from "@/pages/Onboarding";
import AssistantPage from "@/pages/Assistant";
import LettersPage from "@/pages/Letters";
import IRLRevisionPage from "@/pages/IRLRevision";
import AlertsPage from "@/pages/Alerts";
import SubscriptionPage from "@/pages/Subscription";
import PropertiesPage from "@/pages/Properties";
import PropertyDetailPage from "@/pages/PropertyDetail";
import TenantsPage from "@/pages/Tenants";
import LeasesPage from "@/pages/Leases";
import ReceiptsPage from "@/pages/Receipts";
import InspectionsPage from "@/pages/Inspections";
import IncidentsPage from "@/pages/Incidents";
import FinancesPage from "@/pages/Finances";
import SettingsPage from "@/pages/Settings";
import AdminSitePage from "@/pages/AdminSite";
import ContactPage from "@/pages/Contact";
import NotFoundPage from "@/pages/NotFound";

function AuthGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/tarifs" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<AuthGate><LoginPage /></AuthGate>} />
            <Route path="/register" element={<AuthGate><RegisterPage /></AuthGate>} />
            <Route path="/forgot-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/cgu" element={<LegalPage defaultTab="cgu" />} />
            <Route path="/confidentialite" element={<LegalPage defaultTab="confidentialite" />} />
            <Route path="/mentions-legales" element={<LegalPage defaultTab="mentions" />} />
            <Route path="/ressources" element={<ResourcesPage />} />
            <Route path="/ressources/:slug" element={<ArticlePage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            {/* Portail locataire */}
            <Route path="/equipe/activer/:token" element={<MemberActivatePage />} />
            <Route path="/portail/login" element={<PortalLoginPage />} />
            <Route path="/portail/activer/:token" element={<PortalActivatePage />} />
            <Route path="/portail" element={<PortalLayout />}>
              <Route index element={<PortalHomePage />} />
              <Route path="quittances" element={<PortalReceiptsPage />} />
              <Route path="regularisations" element={<PortalRegularisationsPage />} />
              <Route path="bail" element={<PortalLeasePage />} />
              <Route path="incidents" element={<PortalIncidentsPage />} />
              <Route path="courriers" element={<PortalLettersPage />} />
              <Route path="profil" element={<PortalProfilePage />} />
            </Route>
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route
                path="assistant"
                element={
                  <DemoLock
                    title="Assistant IA"
                    description="Votre copilote de gestion locative (rédaction, calculs, conseils) est disponible dès la création de votre compte. Il n'est pas accessible dans la démo."
                  >
                    <AssistantPage />
                  </DemoLock>
                }
              />
              <Route
                path="courriers"
                element={
                  <DemoLock
                    title="Courriers & modèles"
                    description="Nos modèles de courriers et documents sont réservés aux membres. Créez votre compte pour les générer et les personnaliser."
                  >
                    <LettersPage />
                  </DemoLock>
                }
              />
              <Route
                path="revision-irl"
                element={
                  <DemoLock
                    title="Révision IRL"
                    description="Le calcul et la révision de loyer selon l'indice IRL sont réservés aux membres. Créez votre compte pour y accéder."
                  >
                    <IRLRevisionPage />
                  </DemoLock>
                }
              />
              <Route path="alertes" element={
                <DemoLock
                  title="Alertes intelligentes"
                  description="Les alertes automatiques (baux à échéance, impayés, DPE périmé, encadrement des loyers) sont réservées aux membres. Créez votre compte pour en profiter."
                >
                  <AlertsPage />
                </DemoLock>
              } />
              <Route path="abonnement" element={<SubscriptionPage />} />
              <Route path="admin" element={<AdminSitePage />} />
              <Route path="biens" element={<PropertiesPage />} />
              <Route path="biens/:id" element={<PropertyDetailPage />} />
              <Route path="locataires" element={<TenantsPage />} />
              <Route path="baux" element={<LeasesPage />} />
              <Route path="quittances" element={<ReceiptsPage />} />
              <Route path="etats-des-lieux" element={
                <DemoLock
                  title="États des lieux"
                  description="La création d'états des lieux (entrée/sortie, photos, signature) est réservée aux membres. Créez votre compte pour y accéder."
                >
                  <InspectionsPage />
                </DemoLock>
              } />
              <Route path="incidents" element={
                <DemoLock
                  title="Incidents & travaux"
                  description="Le suivi des incidents et travaux est réservé aux membres. Créez votre compte pour le gérer."
                >
                  <IncidentsPage />
                </DemoLock>
              } />
              <Route path="finances" element={<FinancesPage />} />
              <Route path="ressources" element={<AppResourcesPage />} />
              <Route path="parametres" element={<SettingsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
